/**
 * Column Definitions for Matters DataTable
 */

import { StarIcon } from '@heroicons/react/24/outline';

import { CompanionLabel } from '@/components/common/CompanionLabel';
import {
  EmptyCell,
  formatPersonName,
  renderAbsoluteDate,
  renderDuration,
  renderPersonName,
  renderRelativeDate,
  renderSecondaryText,
  TableColumn,
} from '@/components/common/DataTable';
import { StatusPill } from '@/components/common/DataTable/StatusPill';
import {
  FavoriteButtonCompact,
  STAR_ICON_SIZE,
} from '@/components/common/FavoriteButton';
import { MatterListItem, MatterPersonRef } from '@/services/types/matter-types';
import {
  formatDaysRemaining,
  getDaysUntilSol,
  getSolSeverity,
  SOL_SEVERITY_STYLES,
} from '@/utils/solUtils';

import {
  MATTER_COLUMN_CATEGORIES,
  MATTER_DISPLAY_CONFIG,
  MatterColumnIds,
} from './config/columnConfig';
import { ACTIVE_MATTER_STATUSES } from './config/matterStatusConfig';

export interface MatterColumnFilterOptions {
  firmOptions: Array<{ label: string; value: string }>;
  attorneyOptions: Array<{ label: string; value: string }>;
  paralegalOptions: Array<{ label: string; value: string }>;
  caseManagerOptions: Array<{ label: string; value: string }>;
  statusOptions: Array<{ label: string; value: string }>;
  matterTypeOptions: Array<{ label: string; value: string }>;
  preferredLanguageOptions: Array<{ label: string; value: string }>;
}

/**
 * Returns a row className based on SOL proximity.
 * Adds a red left-border accent for matters within 30 days of SOL.
 */
export const getMatterRowClassName = (row: MatterListItem): string => {
  if (row.filed_date) return '';
  const severity = getSolSeverity(row.statute_of_limitations_date);
  return SOL_SEVERITY_STYLES.rowBorder[severity];
};

export const createMatterColumns = ({
  firmOptions,
  attorneyOptions,
  paralegalOptions,
  caseManagerOptions,
  statusOptions,
  matterTypeOptions,
  preferredLanguageOptions,
}: MatterColumnFilterOptions): TableColumn<MatterListItem>[] => [
  // Favorite - disable truncate to prevent overflow:hidden clipping the star
  {
    id: MatterColumnIds.FAVORITE,
    header: <StarIcon className={`${STAR_ICON_SIZE} text-zinc-400`} />,
    accessorKey: 'id',
    width: '48px',
    hideable: false,
    sortable: false,
    truncate: false,
    cell: (row) => (
      <div className="group flex items-center justify-center">
        <FavoriteButtonCompact matterId={row.id} matterName={row.name} />
      </div>
    ),
  },
  // Matter Info
  {
    id: MatterColumnIds.NAME,
    header: 'Matter Name',
    accessorKey: 'name',
    width: '230px',
    hideable: true,
    sortable: true,
    category: MATTER_COLUMN_CATEGORIES.MATTER_INFO,
    sort: {
      options: [
        {
          key: 'clients__first_name',
          ascLabel: 'First Name (A→Z)',
          descLabel: 'First Name (Z→A)',
        },
        {
          key: 'clients__last_name',
          ascLabel: 'Last Name (A→Z)',
          descLabel: 'Last Name (Z→A)',
        },
      ],
    },
    clickable: {
      getHref: (row) => `/matters/${row.id}`,
      renderAddon: (row) => (
        <CompanionLabel
          memberCount={row.companion_count}
          isPrimary={row.is_companion_primary}
          size="sm"
        />
      ),
    },
  },
  {
    id: MatterColumnIds.STATUS,
    header: 'Case Status',
    accessorKey: 'status',
    hideable: true,
    category: MATTER_COLUMN_CATEGORIES.MATTER_INFO,
    cell: (row) => <StatusPill status={row.status} size="sm" />,
    filter:
      statusOptions.length > 0
        ? {
            type: 'multiselect',
            options: statusOptions,
            searchable: true,
            presets: [
              {
                name: 'Active',
                values: ACTIVE_MATTER_STATUSES,
              },
            ],
          }
        : undefined,
  },
  {
    id: MatterColumnIds.MATTER_TYPE,
    header: 'Incident Type',
    accessorKey: 'matter_type',
    width: '220px',
    hideable: true,
    category: MATTER_COLUMN_CATEGORIES.MATTER_INFO,
    cell: (_row, value) => renderSecondaryText(value),
    filter:
      matterTypeOptions.length > 0
        ? { type: 'multiselect', options: matterTypeOptions, searchable: true }
        : undefined,
  },
  {
    id: MatterColumnIds.FIRM_NAME,
    header: 'Firm',
    accessorKey: 'firm_name',
    width: '240px',
    hideable: true,
    category: MATTER_COLUMN_CATEGORIES.MATTER_INFO,
    clickable: {
      getHref: (row) => `/firms/${row.firm.id}`,
      target: '_blank',
    },
    filter:
      firmOptions.length > 0
        ? { type: 'multiselect', options: firmOptions, searchable: true }
        : undefined,
  },

  // Dates
  {
    id: MatterColumnIds.CREATED_AT,
    header: 'Date Created',
    accessorKey: 'created_at',
    sortable: true,
    hideable: true,
    category: MATTER_COLUMN_CATEGORIES.DATES,
    cell: (_row, value) => renderAbsoluteDate(value),
    filter: {
      type: 'daterange',
      searchable: false,
      dateRangeConfig: {
        startKey: 'created_at_after',
        endKey: 'created_at_before',
        startLabel: 'From',
        endLabel: 'To',
      },
    },
  },
  {
    id: MatterColumnIds.DATE_OF_LOSS,
    header: 'Date of Loss',
    accessorKey: 'date_of_loss',
    sortable: true,
    hideable: true,
    category: MATTER_COLUMN_CATEGORIES.DATES,
    cell: (_row, value) => renderAbsoluteDate(value),
    filter: {
      type: 'daterange',
      searchable: false,
      dateRangeConfig: {
        startKey: 'date_of_loss_after',
        endKey: 'date_of_loss_before',
        startLabel: 'From',
        endLabel: 'To',
      },
    },
  },
  {
    id: MatterColumnIds.LAST_ACTIVITY_AT,
    header: 'Last Task Update',
    accessorKey: 'last_activity_at',
    sortable: true,
    hideable: true,
    category: MATTER_COLUMN_CATEGORIES.DATES,
    headerTooltip: 'Most recent task update for this matter',
    cell: (_row, value) =>
      renderRelativeDate(value, {
        staleDaysThreshold: MATTER_DISPLAY_CONFIG.staleDaysThreshold,
      }),
  },
  {
    id: MatterColumnIds.LAST_CONTACTED_AT,
    header: 'Last Client Update',
    accessorKey: 'last_contacted_at',
    sortable: true,
    hideable: true,
    category: MATTER_COLUMN_CATEGORIES.DATES,
    headerTooltip: 'Last time the client was contacted',
    cell: (_row, value) =>
      renderRelativeDate(value, {
        staleDaysThreshold: MATTER_DISPLAY_CONFIG.staleDaysThreshold,
      }),
  },
  {
    id: MatterColumnIds.STATUS_ENTERED_AT,
    header: 'Time in Status',
    accessorKey: 'status_entered_at',
    sortable: true,
    hideable: true,
    category: MATTER_COLUMN_CATEGORIES.DATES,
    headerTooltip: 'How long the matter has been in its current status',
    cell: (_row, value) => renderDuration(value),
  },

  // People
  {
    id: MatterColumnIds.ATTORNEY,
    header: 'Attorney',
    accessorFn: (row) => formatPersonName(row.representing_attorney),
    sortable: true,
    hideable: true,
    category: MATTER_COLUMN_CATEGORIES.PEOPLE,
    cell: (row) =>
      renderPersonName(row.representing_attorney as MatterPersonRef | null),
    filter:
      attorneyOptions.length > 0
        ? { type: 'multiselect', options: attorneyOptions, searchable: true }
        : undefined,
  },
  {
    id: MatterColumnIds.PARALEGAL,
    header: 'Managing Paralegal',
    accessorFn: (row) => formatPersonName(row.managing_paralegal),
    sortable: true,
    hideable: true,
    category: MATTER_COLUMN_CATEGORIES.PEOPLE,
    cell: (row) =>
      renderPersonName(row.managing_paralegal as MatterPersonRef | null),
    filter:
      paralegalOptions.length > 0
        ? { type: 'multiselect', options: paralegalOptions, searchable: true }
        : undefined,
  },
  {
    id: MatterColumnIds.CASE_MANAGER,
    header: 'Case Manager',
    accessorFn: (row) => formatPersonName(row.case_manager),
    sortable: true,
    hideable: true,
    category: MATTER_COLUMN_CATEGORIES.PEOPLE,
    cell: (row) => renderPersonName(row.case_manager as MatterPersonRef | null),
    filter:
      caseManagerOptions.length > 0
        ? { type: 'multiselect', options: caseManagerOptions, searchable: true }
        : undefined,
  },
  // SOL Status
  {
    id: MatterColumnIds.SOL_STATUS,
    header: 'SOL Status',
    accessorKey: 'statute_of_limitations_date',
    sortable: true,
    hideable: true,
    width: '130px',
    category: MATTER_COLUMN_CATEGORIES.DATES,
    headerTooltip: 'Days until statute of limitations expires',
    cell: (row) => {
      // Hide SOL countdown when filed_date is set (filing suit tolls the SOL)
      if (row.filed_date) {
        return (
          <span className="text-zinc-500 text-xs italic font-medium">Tolled</span>
        );
      }
      const days = getDaysUntilSol(row.statute_of_limitations_date);
      if (days === null) {
        return (
          <span className="text-zinc-500 font-medium">
            <EmptyCell />
          </span>
        );
      }
      const severity = getSolSeverity(row.statute_of_limitations_date);
      const label = formatDaysRemaining(days);
      return (
        <span className={SOL_SEVERITY_STYLES.text[severity]}>{label}</span>
      );
    },
    sort: {
      sortKey: 'statute_of_limitations_date',
      labels: {
        asc: 'Soonest First',
        desc: 'Latest First',
      },
      invertArrows: true,
    },
  },
  {
    id: MatterColumnIds.PREFERRED_LANGUAGE,
    header: 'Preferred Language',
    accessorKey: 'preferred_language',
    hideable: false,
    sortable: true,
    category: MATTER_COLUMN_CATEGORIES.MATTER_INFO,
    cell: (_row, value) => renderSecondaryText(value),
    filter:
      preferredLanguageOptions.length > 0
        ? {
            type: 'multiselect',
            options: preferredLanguageOptions,
            searchable: true,
          }
        : undefined,
  },
];
